import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Layout, Menu, Drawer, Tag,
} from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import './SideMenu.less';
import { AuthContext } from 'util/Auth';
import { PermissionsContext, userCanAccessRoute } from 'util/PermissionsContext';
import routeConfig, { MENU_SECTIONS } from 'config/routes';
import { MatrixLogo } from 'components';

const { SubMenu, ItemGroup } = Menu;

const { Sider } = Layout;

const MobileSideMenu = ({ children, open, onClose }) => (
  <Drawer
    id="side-menu"
    placement="left"
    closable={false}
    onClose={onClose}
    visible={open}
    bodyStyle={{ padding: 0 }}
  >
    {children}
  </Drawer>
);

const DesktopSideMenu = ({ children, open }) => (
  <Sider
    id="side-menu"
    trigger={null}
    collapsible
    collapsed={!open}
    theme="light"
  >
    {children}
  </Sider>
);

// Recursively renders menu items based on config (config/routes.js)
const renderItem = ({
  item,
  parentPath,
  handleChange,
  userEmail,
  userIsAdmin,
  userIsExternal,
  permissions,
  showLegacyPages,
}) => {
  // Don't add item to menu if: hidden, menu-only mirror under parent, legacy (when toggle off),
  // external not allowed, or no access (admin/permission/whitelist)
  if (
    item.hidden
    || item.menuHidden
    || (item.legacy && !showLegacyPages)
    || (!item.allowExternal && userIsExternal)
    || !userCanAccessRoute(item, userEmail, permissions, userIsAdmin)
  ) {
    return null;
  }
  const IconComponent = item.icon;
  const icon = IconComponent ? <IconComponent /> : null;
  const currentPath = item.menuPath || `${parentPath}/${item.id}`;

  const visibleSubItems = item.subItems?.filter((subItem) => (
    !subItem.hidden
    && !subItem.menuHidden
    && !(subItem.legacy && !showLegacyPages)
    && !(!subItem.allowExternal && userIsExternal)
    && userCanAccessRoute(subItem, userEmail, permissions, userIsAdmin)
  ));

  // Zero reachable menu children: hide only if this item has no page of its own.
  // menuOnly folders (FRESH Club / Wild Club) must hide — a fake leaf would hit
  // /fresh-club-menu. Items like Products still have a Component; their children
  // are hidden route pages (add/edit/view), not empty folders.
  const hasOwnPage = !!(item.Component || item.url || item.hasNestedSwitch);
  if (visibleSubItems && visibleSubItems.length === 0 && (item.menuOnly || !hasOwnPage)) {
    return null;
  }

  // One visible child (e.g. legacy siblings hidden) → leaf, no folder
  const leafPath = visibleSubItems?.length === 1
    ? (visibleSubItems[0].menuPath || `${currentPath}/${visibleSubItems[0].id}`)
    : currentPath;

  return visibleSubItems && visibleSubItems.length > 1 ? (
    <SubMenu
      key={item.id}
      icon={icon}
      title={item.new ? (
        <span>
          {item.menuLabel}
          <Tag color="blue" className="side-menu-tag">NEW</Tag>
        </span>
      ) : (item.menuLabel)}
      onSelect={(subItem) => handleChange(`${currentPath}/${subItem.key}`)}
    >
      {item.subItems.map((subItem) => (
        renderItem({
          item: subItem,
          parentPath: currentPath,
          handleChange,
          userEmail,
          userIsAdmin,
          userIsExternal,
          permissions,
          showLegacyPages,
        })
      ))}
    </SubMenu>
  ) : (
    <Menu.Item
      key={leafPath}
      icon={icon}
      onClick={item.url ? () => {
        window.location = item.url;
      } : null}
    >
      {item.new ? (
        <span className="side-menu-tag-label">
          <span>{item.menuLabel}</span>
          <Tag color="blue" className="side-menu-tag">NEW</Tag>
        </span>
      ) : (item.menuLabel)}
    </Menu.Item>
  );
};

// TODO: Keep tab highlighted if at a submenu route (e.g. product database -> product editor)
const SideMenu = ({
  open, isMobile, onClose, showLegacyPages,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [selected, setSelected] = useState(location.pathname.replace('/dashboard', ''));

  const { currentUser } = useContext(AuthContext);
  const { permissions } = useContext(PermissionsContext);

  // Update the selected menu item when the pathname changes
  useEffect(() => {
    setSelected(location.pathname.replace('/dashboard', ''));
  }, [location.pathname]);

  const Wrapper = isMobile ? MobileSideMenu : DesktopSideMenu;

  const handleNavigation = (path) => {
    history.push(`/dashboard${path}`);
    if (isMobile) {
      onClose();
    }
  };

  const renderAccessArgs = {
    handleChange: handleNavigation,
    userEmail: currentUser?.email,
    userIsAdmin: currentUser?.admin,
    userIsExternal: currentUser?.external,
    permissions: permissions || [],
    showLegacyPages,
  };

  const renderSectionItems = (sectionId) => routeConfig
    .filter((item) => (item.section ?? null) === sectionId)
    .map((item) => renderItem({
      item,
      parentPath: '',
      ...renderAccessArgs,
    }))
    .filter(Boolean);

  return (
    <div id="side-menu-container">
      <Wrapper
        open={open}
        onClose={onClose}
      >
        <div className="side-menu-header">
          <MatrixLogo />
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[selected]}
          onSelect={(item) => handleNavigation(item.key)}
        >
          {MENU_SECTIONS.map(({ id: sectionId, label }) => {
            const children = renderSectionItems(sectionId);
            if (children.length === 0) return null;
            if (!label) {
              return (
                <React.Fragment key={`section-${sectionId ?? 'root'}`}>
                  {children}
                </React.Fragment>
              );
            }
            return (
              <ItemGroup
                key={`section-${sectionId}`}
                className={`side-menu-group-${sectionId}`}
                title={label}
              >
                {children}
              </ItemGroup>
            );
          })}
        </Menu>
      </Wrapper>
    </div>
  );
};

SideMenu.propTypes = {
  open: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  showLegacyPages: PropTypes.bool,
};

SideMenu.defaultProps = {
  showLegacyPages: false,
};

export default SideMenu;
